import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddAddressComponent } from './add-address/add-address.component';
import { AuthenticationService, UserService } from '../../../core/service';
import { User } from '../../../core/models';
import { GetUserProfileResponse } from '../../../core/responses/GetUserProfileResponse';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { CoreTranslationService } from '../../../../@core/services/translation.service';
import { GetUserAddressesRequest } from '../../../core/requests/GetUserAddressesRequest';
import { GetUserAddressesResponse } from '../../../core/responses/GetUserAddressesResponse';
import { UserAddress } from '../../../core/models/user-address';
import { ChangePasswordRequest } from '../../../core/requests/ChangePasswordRequest';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Patch } from '../../../core/models/patch';
import { BaseResponse } from '../../../core/responses/BaseResponse';
import { GetCardInfoRequest } from '../../../core/requests/GetCardInfoRequest';
import { CARD_STATUS, phonePattern, maskPhoneNumber } from '../../../core/helpers/constants';
import { GetCardInfoResponse } from '../../../core/responses/GetCardInfoResponse';
import { BenefitSummaryService } from '../../../core/service/benefit-summary.service';
import { Card } from '../../../core/models/card';
import { FilePostRequest } from '../../../core/requests/FilePostRequest';
import { ActivatedRoute } from '@angular/router';
import {
  confirmPassword,
  createPasswordStrengthValidator,
} from '../../../core/helpers/custom-validators';
import { ToastrService } from 'ngx-toastr';
import { FileValidationService } from 'app/core/service/fileValidation.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  providers: [TranslatePipe],
})
export class ProfileComponent implements OnInit, OnDestroy {
  public userProfile: User = null;
  public billingAddresses: Array<UserAddress> = null;
  public shippingAddresses: Array<UserAddress> = null;
  private ngUnsubscribe = new Subject<void>();
  public selectedTab = 0;
  public address: any = null;
  public changePasswordForm: FormGroup;
  public cards: Array<Card> = null;
  public profilePictureBase64 = '';
  public profilePictureName = '';
  public profilePictureSrc = 'assets/images/avatars/no-user-image-icon.jpg';
  public subdomain: string;
  public companyHrEmail = '';
  public disableButton = false;
  public canChangePassword = false;
  public userPhoneNumber: string = '';
  public phoneError: string = '';
  // Visibility toggles for masked sensitive fields (CNP/email/phone) - shown
  // masked by default, revealed via the eye icon.
  public showCnp = false;
  public showEmail = false;
  public showPhoneNumber = false;
  public showEmployeeCode = false;
  public showHrEmail = false;

  // Masks a phone number for read-only display (e.g. address cards), keeping
  // only the last 4 digits visible.
  public maskPhone(phone: string): string {
    return maskPhoneNumber(phone);
  }

  constructor(
    private modalService: NgbModal,
    private _coreTranslationService: CoreTranslationService,
    private userService: UserService,
    private translate: TranslatePipe,
    private _toastrService: ToastrService,
    private benefitSummaryService: BenefitSummaryService,
    private fb: FormBuilder,
    private authenticationService: AuthenticationService,
    private fileValidationService: FileValidationService,
    private _route: ActivatedRoute
  ) {
    // this._coreTranslationService.translate(english, romanian);
    this.subdomain = this._route.snapshot.params['subdomain'];
    this.changePasswordForm = new FormGroup(
      {
        newPassword: new FormControl('', [
          Validators.required,
          (control: AbstractControl) =>{ return createPasswordStrengthValidator(control, this.userProfile?.email)}
        ]),
        confirmPassword: new FormControl('', [Validators.required]),
        oldPassword: new FormControl('', [Validators.required]),
      },
      {
        validators: [confirmPassword('newPassword', 'confirmPassword')],
      }
    );
    if (this.authenticationService.currentUserCompanyInfoValue) {
      this.companyHrEmail =
        this.authenticationService.currentUserCompanyInfoValue.companyHrEmail;
    }
  }

  ngOnInit(): void {
    this.getUserProfile();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.changePasswordForm.controls;
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Get user profile
   */
  public getUserProfile() {
    this.userService
      .getUserProfile(this.authenticationService.currentUserValue.userId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onGetUserProfileSuccess(data);
        },
        (error) => {
          this.onGetUserProfileFail(error);
        }
      );
  }

  public onGetUserProfileSuccess(data: GetUserProfileResponse) {
    this.userProfile = data.responseObject;
    this.userPhoneNumber = data.responseObject.phoneNumber;
    if (this.userProfile.profilePicturePhotoUrl) {
      this.profilePictureSrc = this.userProfile.profilePicturePhotoUrl;
    }
    this.canChangePassword = this.userProfile.isFederated === false || this.authenticationService.currentUserCompanyInfoValue.isSsoEnabled === false;
  }

  public onGetUserProfileFail(error: HttpErrorResponse) {}

  /**
   * Get user addresses
   */
  public getUserAddresses(type: string) {
    const params: GetUserAddressesRequest = {
      paginationCriteria: {
        start: 0,
        limit: 5,
      },
      userId: this.authenticationService.currentUserValue.userId,
      addressType: type === 'billing' ? 2 : 1,
    };
    this.userService
      .getUserAddresses(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onGetUserAddressesSuccess(data, type);
        },
        (error) => {
          this.onGetUserAddressesFail(error);
        }
      );
  }

  public onGetUserAddressesSuccess(
    data: GetUserAddressesResponse,
    type: string
  ) {
    if (type === 'billing') {
      this.billingAddresses = data.responseObject.data;
      this.disableButton = this.billingAddresses?.length >= 5;
    } else {
      this.shippingAddresses = data.responseObject.data;
      this.disableButton = this.shippingAddresses?.length >= 5;
    }
  }

  public onGetUserAddressesFail(error: HttpErrorResponse) {}


  /**
   * Delete user address
   */
  public deleteAddress(addressId: number, type: string) {
    const params: Array<Patch> = [
      {
        op: 'replace',
        path: 'isDeleted',
        value: 'true',
      },
    ];

    this.userService
      .deleteAddresses(params, addressId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onDeleteSuccess(data, type, addressId);
        },
        (error) => {
          this.onDeleteFail(error);
        }
      );
  }

  public onDeleteSuccess(data: BaseResponse, type, id: number) {
    if (type === 'shipping') {
      this.shippingAddresses = this.shippingAddresses.filter((addressItem) => addressItem.id !== id);
      this.disableButton = this.shippingAddresses?.length >= 5;
    } else {
      this.billingAddresses = this.billingAddresses.filter((addressItem) => addressItem.id !== id);
      this.disableButton = this.billingAddresses?.length >= 5;
    }
  }

  public onDeleteFail(error: HttpErrorResponse) {}

  /**
   * Change picture
   */
  public changePicture() {
    const params: FilePostRequest = {
      filePostModel: {
        name: this.profilePictureName,
        base64Value: this.profilePictureBase64,
      },
      pictureSettings: {
        compress: false,
        resize: true,
        width: 89,
        height: 89,
      },
    };
    this.userService
      .changeProfilePicture(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onChangePictureSuccess(data);
        },
        (error) => {
          this.onChangePictureFail(error);
        }
      );
  }

  public onChangePictureSuccess(data) {
    this.profilePictureSrc = data.responseObject;
    this.authenticationService.updateCurrentUserInfo();
    // @ts-ignore
    document.getElementById('profile-picture-input').value = null;
  }

  public onChangePictureFail(error: any) {
    if (error && error.messages) {
      error.messages.forEach((errorItem) => {
        this._toastrService.error(
          this.translate.transform(errorItem),
          this.translate.transform('ERROR'),
          { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
        );
      });
    }
  }

  /**
   * Delete picture
   */
  public deletePicture() {
    this.userService
      .deleteProfilePicture()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onDeletePictureSuccess();
        },
        (error) => {
          this.onDeletePictureFail(error);
        }
      );
  }

  public onDeletePictureSuccess() {
    this.authenticationService.updateCurrentUserInfo();
    this.profilePictureSrc = 'assets/images/avatars/no-user-image-icon.jpg';
    this.profilePictureBase64 = '';
    this.profilePictureName = '';
  }

  public onDeletePictureFail(error: HttpErrorResponse) {}

  /**
   * Change password
   */
  public changePassword() {
    const params: ChangePasswordRequest = {
      oldPassword: this.changePasswordForm.get('oldPassword')?.value,
      newPassword: this.changePasswordForm.get('newPassword')?.value,
      confirmPassword: this.changePasswordForm.get('confirmPassword')?.value,
    };

    this.userService
      .changePassword(params)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (data) => {
          this.onChangePasswordSuccess();
        },
        (error) => {
          this.onChangePasswordFail(error);
        }
      );
  }

  public onChangePasswordSuccess() {
    this._toastrService.success(
      this.translate.transform('CHANGE_PASS.PASSWORD_CHANGED_SUCCESS'),
      this.translate.transform('SUCCESS'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
    this.changePasswordForm.patchValue({
      newPassword: '',
      confirmPassword: '',
      oldPassword: '',
    });
    this.changePasswordForm.markAsUntouched();
    this.changePasswordForm.markAsPristine();
  }

  public onChangePasswordFail(error: any) {
    error.messages.forEach((errorItem) => {
      this._toastrService.error(
        this.translate.transform(errorItem),
        this.translate.transform('ERROR'),
        { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
      );
    });
  }

  /**
   * Change phone number
   */
  public changePhoneNumber() {

    if (!phonePattern.test(this.userPhoneNumber)) {
      this.phoneError = this.translate.transform('PROFILE.SAVE_PHONE_NUMBER_ERROR');
      return;
    }

    this.phoneError = '';

    const currentPhoneNumber = this.userProfile.phoneNumber;
    if (currentPhoneNumber === this.userPhoneNumber) {
      return;
    }

    const patchData: Array<Patch> = [{
      op: 'replace',
      path: 'phoneNumber',
      value: this.userPhoneNumber,
    }];

    this.userService
      .editUser(patchData, this.authenticationService.currentUserValue.userId)
      .subscribe(
        () => this.changePhoneNumberSuccess(),
        (error) => this.changePhoneNumberFail(error)
      );
  }

  public changePhoneNumberSuccess() {
    this.userProfile.phoneNumber = this.userPhoneNumber;
    this._toastrService.success(
      this.translate.transform(
        'PROFILE.SAVE_PHONE_NUMBER_SUCCESS'
      ),
      this.translate.transform('SUCCESS'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
  }

  public changePhoneNumberFail(error: any) {
    this._toastrService.success(
      this.translate.transform(
        'PROFILE.SAVE_PHONE_NUMBER_FAIL'
      ),
      this.translate.transform('ERROR'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
  }

  /**
   * Helpers
   */

  public selectTab(tabIndex) {
    this.selectedTab = tabIndex;
    switch (this.selectedTab) {
      case 0:
        this.getUserProfile();
        break;
      case 2:
        this.getUserAddresses('shipping');
        break;
      case 3:
        this.getUserAddresses('billing');
        break;
      case 4:
      case 5:
        break;
      default:
        this.getUserProfile();
        break;
    }
  }

  public addEditAddress(
    type: string,
    action: string,
    address: UserAddress | null = null
  ) {
    const modalRef = this.modalService.open(AddAddressComponent, {
      centered: true,
      backdrop: 'static',
    });
    modalRef.componentInstance.actionType = action;
    modalRef.componentInstance.addressType = type;
    modalRef.componentInstance.selectedAddress = address;

    modalRef.result.then((response) => {
      if (response[0]) {
        if (action === 'edit') {
          this.billingAddresses.forEach((addressItem) => {
            if (addressItem.id === response[1].id) {
              addressItem = response[1];
            }
          });
        } else {
          if (type === 'shipping') {
            this.getUserAddresses('shipping');
          } else {
            this.getUserAddresses('billing');
          }
        }
      }
    });
  }

  public async handlePictureUpload(event) {
    const file = event.target.files.item(0);

    if (!file) {
      return;
    }

    const validation = await this.fileValidationService.validateImage(file);

    if (!validation.valid) {
      const errorMessage = validation.params
      ? this.translate.transform(validation.message, validation.params)
      : this.translate.transform(validation.message);

    this._toastrService.error(
      errorMessage,
      this.translate.transform('ERROR'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );

      event.target.value = '';
      return;
    }

    const base64Value: any = await this.getBase64(file);
    this.profilePictureName = file.name;
    this.profilePictureBase64 = base64Value;
    this.changePicture();
  }

  public getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }
}
